/**
 * A list that goes on both ways — the phone's Month and List (Aaron, Oct 25,
 * phone notes 3.2 and 3.3: Outlook's month after month, and its agenda).
 *
 * The page draws a window of sections (a month each, or a day each), every
 * one carrying `attr` with its key, inside a `relative` scroller. This keeps
 * the section at the top where it is whatever happens around it — a month
 * added above, a month dropped below, a month's things arriving and making
 * its days taller — by remembering which section was at the top and how far
 * down, and putting it back after every render. Without that, adding the
 * past above the screen pushes what somebody is reading down by the height
 * of everything added, and Safari has no scroll anchoring of its own to do
 * it (`overflow-anchor` is off here so Chrome's does not do it twice).
 *
 * Near either end it asks for more; the page widens its window and, past its
 * cap, drops the far end — the idea of the app's phone lists (`ListEnd`, a
 * sentinel that loads as it comes into view), not the list engine.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

export interface EndlessOptions {
  /** The attribute each section carries its key in: `data-day`, `data-month`. */
  attr: string
  /** Within this many pixels of the top: ask for more above. */
  onNearTop: () => void
  onNearBottom: () => void
  /** The section at the top changed because somebody scrolled. */
  onTopChange?: (key: string) => void
  /** How near an end counts as near (px). */
  margin?: number
}

interface Anchor { key: string; offset: number }

export function useEndless(scroller: RefObject<HTMLElement | null>, options: EndlessOptions) {
  const anchor = useRef<Anchor | null>(null)
  const latest = useRef(options)
  latest.current = options
  const margin = options.margin ?? 900

  const sections = useCallback((): HTMLElement[] => {
    const box = scroller.current
    if (!box) return []
    return [...box.querySelectorAll<HTMLElement>(`[${latest.current.attr}]`)]
  }, [scroller])

  /** The section whose box holds the top of the scroller, found by halving (a year of days is 365 of them). */
  const topSection = useCallback((): Anchor | null => {
    const box = scroller.current
    const list = sections()
    if (!box || list.length === 0) return null
    const at = box.scrollTop + 1
    let low = 0
    let high = list.length - 1
    while (low < high) {
      const mid = Math.ceil((low + high) / 2)
      if (list[mid]!.offsetTop <= at) low = mid
      else high = mid - 1
    }
    const section = list[low]!
    return { key: section.getAttribute(latest.current.attr) ?? '', offset: section.offsetTop - box.scrollTop }
  }, [scroller, sections])

  const nearEnds = useCallback((laidOut: boolean) => {
    const box = scroller.current
    if (!box) return
    /* Before the browser has laid the scroller out every measure is nought, and nought is "near" both ends. */
    if (laidOut && box.clientHeight === 0) return
    /* Nothing drawn (nobody's calendar shown) is no end to load more at. */
    if (!box.querySelector(`[${latest.current.attr}]`)) return
    if (box.scrollTop < margin) latest.current.onNearTop()
    if (box.scrollHeight - box.scrollTop - box.clientHeight < margin) latest.current.onNearBottom()
  }, [scroller, margin])

  /*
   * Put the section that was at the top back where it was. Every height that
   * changes goes through here before anything reads the top again: a render
   * (the layout effect below), anything that resizes a section without one —
   * a face's font arriving, a line wrapping differently — (the observer), and
   * a scroll in the same frame as either (the scroll handler checks the
   * height it last put right first), so a drift is never written down as
   * where somebody is reading.
   */
  const measured = useRef(-1)
  const restore = useCallback(() => {
    const box = scroller.current
    const held = anchor.current
    if (!box) return
    if (held) {
      const section = box.querySelector<HTMLElement>(`[${latest.current.attr}="${held.key}"]`)
      if (section) {
        const wanted = section.offsetTop - held.offset
        if (Math.abs(box.scrollTop - wanted) >= 1) box.scrollTop = wanted
      }
    }
    measured.current = box.scrollHeight
  }, [scroller])

  /* Somebody scrolled: remember what is at the top, say so when it changed, and load at the ends. */
  useEffect(() => {
    const box = scroller.current
    if (!box) return undefined
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        if (box.scrollHeight !== measured.current) restore()
        const top = topSection()
        if (top) {
          const was = anchor.current?.key
          anchor.current = top
          if (top.key !== was) latest.current.onTopChange?.(top.key)
        }
        nearEnds(false)
      })
    }
    box.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      box.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [scroller, topSection, nearEnds, restore])

  /* A section that changes size on its own (no render of ours) is put right before it is painted. */
  const observer = useRef<ResizeObserver | null>(null)
  const watched = useRef(new Set<Element>())
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined
    const seen = watched.current
    const watching = new ResizeObserver(() => restore())
    observer.current = watching
    for (const one of scroller.current?.querySelectorAll(`[${latest.current.attr}]`) ?? []) {
      watching.observe(one)
      seen.add(one)
    }
    return () => {
      watching.disconnect()
      observer.current = null
      seen.clear()
    }
  }, [scroller, restore])

  /* After every render: the section that was at the top goes back where it was, then the ends are looked at. */
  useLayoutEffect(() => {
    restore()
    const box = scroller.current
    const watching = observer.current
    if (box && watching) {
      for (const one of watched.current) {
        if (!one.isConnected) { watching.unobserve(one); watched.current.delete(one) }
      }
      for (const one of box.querySelectorAll(`[${latest.current.attr}]`)) {
        if (!watched.current.has(one)) { watching.observe(one); watched.current.add(one) }
      }
    }
    nearEnds(true)
  })

  /** Put a section at the top (`offset` px down) — opening on a day, or a day picked elsewhere. */
  const scrollToKey = useCallback((key: string, offset = 0) => {
    anchor.current = { key, offset }
    restore()
  }, [restore])

  /** The key of the section at the top now. */
  const topKey = useCallback(() => anchor.current?.key ?? null, [])

  return { scrollToKey, topKey }
}
